import Alert from '@mui/material/Alert'
import Collapse from '@mui/material/Collapse'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import _ from 'lodash'
import { Fragment } from 'react'
import { TransitionGroup } from 'react-transition-group'
import { generateRandomKey } from 'utils/random'
import { create } from 'zustand'

type Message = string | string[]

const Snackbars = () => {
  const toasts = useStore((state) => state.toasts)

  const getMessage = (messages: Message) => {
    if (_.isArray(messages)) {
      return (
        <>
          {messages.map((message, index) => (
            <Fragment key={index}>
              {message}
              {index < messages.length - 1 && <br />}
            </Fragment>
          ))}
        </>
      )
    }
    return messages
  }

  return (
    <StyledStack>
      <TransitionGroup component={null}>
        {toasts.map((toast) => (
          <Collapse key={toast.id}>
            <Alert severity={toast.severity} variant="standard">
              {getMessage(toast.message)}
            </Alert>
          </Collapse>
        ))}
      </TransitionGroup>
    </StyledStack>
  )
}

// Styles
const StyledStack = styled(Stack)(
  ({ theme }) => `
  display: flex;
  gap: 12px;
  position: fixed;
  right: 24px;
  top: 24px;
  width: 300px;
  z-index: ${theme.zIndex.snackbar};
`
)

// Types
interface Toast {
  id: string
  message: Message
  severity: Severity
}

type Severity = 'success' | 'error' | 'warning' | 'info'

interface Store {
  toasts: Toast[]
  add: (message: Message, severity: Severity) => Toast
  remove: (id: string) => void
}

// State
const useStore = create<Store>((set) => ({
  toasts: [],
  add: (message, severity) => {
    const toast = createToast(message, severity)
    set((state) => ({ toasts: [toast, ...state.toasts] }))
    return toast
  },
  remove: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id),
    })),
}))

// Hooks
const useToast = () => {
  const add = useStore((state) => state.add)
  const remove = useStore((state) => state.remove)
  const removeTimeout = 5000 // ms

  function showSuccess(message: Message) {
    const { id } = add(message, 'success')
    setTimeout(() => remove(id), removeTimeout)
  }

  function showError(message: Message) {
    const { id } = add(message, 'error')
    setTimeout(() => remove(id), removeTimeout)
  }

  function showWarning(message: Message) {
    const { id } = add(message, 'warning')
    setTimeout(() => remove(id), removeTimeout)
  }

  function showInfo(message: Message) {
    const { id } = add(message, 'info')
    setTimeout(() => remove(id), removeTimeout)
  }

  return { showSuccess, showError, showWarning, showInfo }
}

// Utils
function createToast(message: Message, severity: Severity): Toast {
  return {
    id: generateRandomKey(10),
    message,
    severity,
  }
}

// Exports
export default Snackbars
export { useToast }
